/**
 * Couverture d'achat du bilan des questions gratuites (exercice relié à une
 * SKILLCARD, web) : remplace l'illustration du haut pour que l'appel à
 * débloquer soit visible SANS défiler, même sur un petit écran. Teinte et
 * créature de la carte ; le bouton mène à la page d'achat normale.
 */

import type { CSSProperties } from 'react';
import { Lock } from 'lucide-react';
import { cardPalette, formatEuros } from '@jeraisonne/db/cards';
import { MathText } from '@jeraisonne/ui';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import { usePublicCard } from '@/lib/cards/api';
import { useSkillcardOffers } from '@/lib/skillcards/purchase';

export function SkillcardUnlockCover({
  cardId,
  userId,
  remaining,
  onUnlock,
}: {
  cardId: string;
  userId: string | null;
  /** Questions encore verrouillées dans cet exercice. */
  remaining: number;
  onUnlock: () => void;
}) {
  const card = usePublicCard(cardId).data?.card;
  const offers = useSkillcardOffers(cardId, userId).data;
  const palette = cardPalette(card?.levels ?? [], card?.color_theme ?? 'night');
  const vars = { '--jr-c-accent': palette.accent, '--jr-c-bg': palette.bg } as CSSProperties;
  // Prix d'appel : la carte seule (le moins cher).
  const price = offers?.card && offers.card.cards_to_buy > 0 ? offers.card.price_cents : null;

  return (
    <section
      data-testid="skillcard-unlock-cover"
      aria-label="Débloquer la SKILLCARD"
      style={vars}
      className="animate-scale-in relative overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--jr-c-accent)_55%,transparent)] bg-[radial-gradient(circle_at_20%_30%,color-mix(in_srgb,var(--jr-c-accent)_40%,var(--jr-c-bg))_0%,var(--jr-c-bg)_75%)] p-5 text-center shadow-lg sm:p-7"
    >
      {card && (
        <OrigamiCreature
          creature={card.creature}
          accent={palette.accent}
          className="mx-auto aspect-square h-16 drop-shadow-[0_8px_18px_rgba(0,0,0,0.45)] sm:h-20"
        />
      )}
      <p className="font-display mt-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[var(--jr-c-accent)]">
        SKILLCARD{card ? ' · ' : ''}
        {card && <MathText>{card.title}</MathText>}
      </p>
      {remaining > 0 && (
        <p className="font-display mt-2 text-2xl font-extrabold uppercase leading-tight text-white sm:text-3xl">
          Encore {remaining} question{remaining > 1 ? 's' : ''} !
        </p>
      )}
      <p className="mt-1 text-sm text-white/85">
        Débloque la SKILLCARD et accède à tous ses exercices pendant 1 an.
      </p>
      <button
        type="button"
        onClick={onUnlock}
        className="font-display mt-4 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl bg-[var(--jr-c-accent)] px-5 py-3 text-base font-extrabold text-[var(--jr-c-bg)] shadow-lg transition-transform active:scale-[0.98] sm:w-auto"
      >
        <Lock className="h-4 w-4" aria-hidden />
        {price !== null ? `Débloquer dès ${formatEuros(price)}` : 'Débloquer la SKILLCARD'}
      </button>
    </section>
  );
}
